/**
 * CONSAI Business App - Customers Management Page
 * IMPROVEMENT 2: WhatsApp Summary Button
 * IMPROVEMENT 3: Customer Activity Timeline
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from '../components/ui/sheet';
import { toast } from 'sonner';
import {
  Users, User, Plus, Search, Edit2, Trash2, Phone, Mail, MapPin, ChevronLeft, ChevronRight,
  Eye, MessageCircle, ShoppingCart, Receipt, BookOpen, CreditCard, Calendar, ArrowUpRight, ArrowDownLeft
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import MobileNav from '../components/MobileNav';
import { AetherPage } from '../components/AetherPage';
import PlanExpiredModal from '../components/PlanExpiredModal';
import Header from '../components/Header';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Customers = () => {
  const { language } = useLanguage();
  const { user, isPlanExpired } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [showPlanExpired, setShowPlanExpired] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ name: '', mobile_number: '', email: '', address: '', notes: '' });
  
  // Customer Profile View State (IMPROVEMENTS 2 & 3)
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showProfile, setShowProfile] = useState(false);
  const [customerActivity, setCustomerActivity] = useState([]);
  const [activityFilter, setActivityFilter] = useState('all');
  const [activityLoading, setActivityLoading] = useState(false);

  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, per_page: 20 });
      if (search) params.append('search', search);
      const res = await axios.get(`${API_URL}/api/customers?${params}`, { withCredentials: true });
      setCustomers(res.data.customers);
      setTotal(res.data.total);
    } catch (err) {
      toast.error(language === 'hi' ? 'ग्राहक लोड करने में विफल' : 'Failed to load customers');
    }
    setLoading(false);
  }, [page, search, language]);

  useEffect(() => { fetchCustomers(); }, [fetchCustomers]);

  // IMPROVEMENT 3: Fetch customer activity
  const fetchCustomerActivity = async (customerId) => {
    setActivityLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/customers/${customerId}/activity`, { withCredentials: true });
      setCustomerActivity(res.data.activities || []);
    } catch (err) {
      console.error('Failed to load activity:', err);
      setCustomerActivity([]);
    }
    setActivityLoading(false);
  };

  // Open customer profile with activity
  const openCustomerProfile = async (customer) => {
    setSelectedCustomer(customer);
    setShowProfile(true);
    setActivityFilter('all');
    await fetchCustomerActivity(customer.customer_id);
  };

  // IMPROVEMENT 2: WhatsApp Summary
  const sendWhatsAppSummary = (customer) => {
    if (!customer.mobile_number) {
      toast.error(language === 'hi' ? 'मोबाइल नंबर नहीं है' : 'No mobile number available');
      return;
    }
    
    const businessName = user?.business_name || 'Our Business';
    const totalPurchases = (customer.total_purchases || 0).toLocaleString('en-IN');
    const creditBalance = (customer.total_credit || 0).toLocaleString('en-IN');
    
    let message = `Hi ${customer.name},\n\n`;
    message += `Here's a summary of your account with ${businessName}:\n\n`;
    message += `Total Purchases: ₹${totalPurchases}\n`;
    if (customer.total_credit > 0) {
      message += `Pending Payment: ₹${creditBalance}\n`;
    }
    message += `\nThank you for your business! 🙏\n- ${businessName}`;
    
    const phoneNumber = customer.mobile_number.replace(/\D/g, '');
    const waNumber = phoneNumber.startsWith('91') ? phoneNumber : `91${phoneNumber}`;
    const waUrl = `https://wa.me/${waNumber}?text=${encodeURIComponent(message)}`;
    
    window.open(waUrl, '_blank');
  };

  // Filter activity by type
  const filteredActivity = customerActivity.filter(item => {
    if (activityFilter === 'all') return true;
    return item.type === activityFilter;
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) { toast.error('Name is required'); return; }
    try {
      if (editingCustomer) {
        await axios.put(`${API_URL}/api/customers/${editingCustomer.customer_id}`, formData, { withCredentials: true });
        toast.success(language === 'hi' ? 'ग्राहक अपडेट हुआ' : 'Customer updated');
      } else {
        await axios.post(`${API_URL}/api/customers`, formData, { withCredentials: true });
        toast.success(language === 'hi' ? 'ग्राहक जोड़ा गया' : 'Customer added');
      }
      setShowForm(false);
      setEditingCustomer(null);
      setFormData({ name: '', mobile_number: '', email: '', address: '', notes: '' });
      fetchCustomers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(language === 'hi' ? 'क्या आप इसे हटाना चाहते हैं?' : 'Delete this customer?')) return;
    try {
      await axios.delete(`${API_URL}/api/customers/${id}`, { withCredentials: true });
      toast.success(language === 'hi' ? 'ग्राहक हटा दिया गया' : 'Customer deleted');
      fetchCustomers();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const openEdit = (c) => {
    setEditingCustomer(c);
    setFormData({ name: c.name, mobile_number: c.mobile_number || '', email: c.email || '', address: c.address || '', notes: c.notes || '' });
    setShowForm(true);
  };

  const totalPages = Math.ceil(total / 20);
  const plan = user?.current_plan || 'starter';
  const maxCustomers = plan === 'starter' ? 50 : -1;

  const custBadge = (
    <span className="bg-[var(--aether-surface-high)] text-white/65 px-2 py-0.5 rounded text-[12px] font-bold" data-testid="customer-count-badge">
      {total}{maxCustomers !== -1 ? `/${maxCustomers}` : ''}
    </span>
  );
  const custAddBtn = (
    <Button
      onClick={() => {
        if (isPlanExpired()) { setShowPlanExpired(true); return; }
        setEditingCustomer(null);
        setFormData({ name: '', mobile_number: '', email: '', address: '', notes: '' });
        setShowForm(true);
      }}
      className="bg-[#021945] hover:bg-[#021945]/90"
      data-testid="add-customer-btn"
    >
      <Plus className="h-4 w-4 mr-1" />
      {language === 'hi' ? 'जोड़ें' : 'Add'}
    </Button>
  );

  return (
    <AetherPage
      title={language === 'hi' ? 'ग्राहक' : 'Customers'}
      icon={Users}
      iconColor="#ffffff"
      titleTestId="customers-title"
      badge={custBadge}
      rightElement={custAddBtn}
      mainClassName="p-4 md:p-8 space-y-4"
    >
          {/* Search */}
          <div className="relative" data-testid="customer-search-container">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder={language === 'hi' ? 'नाम, मोबाइल, ईमेल से खोजें...' : 'Search by name, mobile, email...'}
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="pl-10"
              data-testid="customer-search-input"
            />
          </div>

          {/* Customer List */}
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin h-8 w-8 border-4 border-white/70 border-t-transparent rounded-full" />
            </div>
          ) : customers.length === 0 ? (
            <div className="text-center py-12 border border-dashed border-[var(--aether-outline-variant)] rounded-xl">
              <Users className="h-12 w-12 text-white/20 mx-auto mb-4" />
              <p className="text-white/55">{language === 'hi' ? 'कोई ग्राहक नहीं मिला' : 'No customers found'}</p>
            </div>
          ) : (
            <div className="space-y-4" data-testid="customer-list">
              {customers.map((c) => (
                <div
                  key={c.customer_id}
                  className="glass-card glass-card-hoverable executive-border p-5 md:p-6 flex items-center justify-between gap-4 group border-l-2"
                  style={{ borderLeftColor: '#06B6D4' }}
                  data-testid={`customer-card-${c.customer_id}`}
                >
                  <div className="flex items-center gap-4 md:gap-6 flex-1 min-w-0">
                    <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-[var(--aether-surface-highest)] flex-shrink-0 flex items-center justify-center border border-[var(--aether-outline-variant)]">
                      <User className="h-6 w-6 md:h-7 md:w-7 text-white/65" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="headline-sm text-white truncate">{c.name}</h3>
                      <div className="flex flex-wrap gap-3 mt-1 text-sm text-white/55">
                        {c.mobile_number && <span className="flex items-center gap-1"><Phone className="h-3.5 w-3.5" />{c.mobile_number}</span>}
                        {c.email && <span className="hidden md:flex items-center gap-1"><Mail className="h-3.5 w-3.5" />{c.email}</span>}
                        {c.address && <span className="hidden md:flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{c.address}</span>}
                      </div>
                    </div>
                  </div>

                  <div className="hidden md:flex items-center gap-8">
                    <div className="text-right">
                      <p className="label-caps text-[10px] text-white/45 mb-1">{language === 'hi' ? 'कुल खरीद' : 'TOTAL PURCHASES'}</p>
                      <p className="headline-sm text-white">₹{(c.total_purchases || 0).toLocaleString('en-IN')}</p>
                    </div>
                    <div className="text-right min-w-[100px]">
                      <p className="label-caps text-[10px] text-white/45 mb-1">{language === 'hi' ? 'उधार' : 'CREDIT'}</p>
                      <p className={`headline-sm ${c.total_credit > 0 ? 'text-red-300' : 'text-white'}`}>₹{(c.total_credit || 0).toLocaleString('en-IN')}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-1 md:gap-2 flex-shrink-0">
                    <button onClick={() => openCustomerProfile(c)}
                      className="w-9 h-9 rounded-lg flex items-center justify-center text-white/55 hover:text-white hover:bg-white/5 transition-all"
                      data-testid={`view-customer-${c.customer_id}`}>
                      <Eye className="h-4 w-4" />
                    </button>
                    <button onClick={() => openEdit(c)}
                      className="w-9 h-9 rounded-lg flex items-center justify-center text-white/55 hover:text-white hover:bg-white/5 transition-all"
                      data-testid={`edit-customer-${c.customer_id}`}>
                      <Edit2 className="h-4 w-4" />
                    </button>
                    <button onClick={() => handleDelete(c.customer_id)}
                      className="w-9 h-9 rounded-lg flex items-center justify-center text-white/55 hover:text-red-400 hover:bg-red-500/10 transition-all"
                      data-testid={`delete-customer-${c.customer_id}`}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center gap-2 pt-4" data-testid="customer-pagination">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="flex items-center text-sm text-slate-600">{page} / {totalPages}</span>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}

      {/* Add/Edit Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-manrope">
              {editingCustomer
                ? (language === 'hi' ? 'ग्राहक अपडेट करें' : 'Edit Customer')
                : (language === 'hi' ? 'नया ग्राहक जोड़ें' : 'Add Customer')}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-3">
            <Input placeholder={language === 'hi' ? 'नाम *' : 'Name *'} value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} data-testid="customer-name-input" className="" />
            <Input placeholder={language === 'hi' ? 'मोबाइल नंबर' : 'Mobile Number'} value={formData.mobile_number} onChange={(e) => setFormData({ ...formData, mobile_number: e.target.value })} data-testid="customer-mobile-input" className="" />
            <Input placeholder={language === 'hi' ? 'ईमेल' : 'Email'} value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} data-testid="customer-email-input" className="" />
            <Input placeholder={language === 'hi' ? 'पता' : 'Address'} value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} data-testid="customer-address-input" className="" />
            <Input placeholder={language === 'hi' ? 'नोट्स' : 'Notes'} value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} data-testid="customer-notes-input" className="" />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)} className="">{language === 'hi' ? 'रद्द करें' : 'Cancel'}</Button>
              <Button type="submit" className="bg-[#021945] hover:bg-[#021945]/90" data-testid="customer-submit-btn">
                {editingCustomer ? (language === 'hi' ? 'अपडेट करें' : 'Update') : (language === 'hi' ? 'जोड़ें' : 'Add')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* IMPROVEMENT 2 & 3: Customer Profile Sheet */}
      <Sheet open={showProfile} onOpenChange={setShowProfile}>
        <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto">
          {selectedCustomer && (
            <>
              <SheetHeader className="pb-4 border-b">
                <SheetTitle className="font-manrope text-xl">{selectedCustomer.name}</SheetTitle>
                <div className="flex flex-wrap gap-2 text-sm text-slate-500">
                  {selectedCustomer.mobile_number && (
                    <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{selectedCustomer.mobile_number}</span>
                  )}
                  {selectedCustomer.email && (
                    <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{selectedCustomer.email}</span>
                  )}
                </div>
              </SheetHeader>

              {/* Summary Cards */}
              <div className="grid grid-cols-2 gap-3 py-4">
                <Card className="bg-green-50 border-green-200">
                  <CardContent className="p-3">
                    <p className="text-xs text-green-600">{language === 'hi' ? 'कुल खरीदारी' : 'Lifetime Value'}</p>
                    <p className="text-lg font-bold text-green-700 font-manrope">₹{(selectedCustomer.total_purchases || 0).toLocaleString('en-IN')}</p>
                  </CardContent>
                </Card>
                <Card className={`${selectedCustomer.total_credit > 0 ? 'bg-red-50 border-red-200' : 'bg-slate-50'}`}>
                  <CardContent className="p-3">
                    <p className={`text-xs ${selectedCustomer.total_credit > 0 ? 'text-red-600' : 'text-slate-600'}`}>
                      {language === 'hi' ? 'उधार बकाया' : 'Credit Balance'}
                    </p>
                    <p className={`text-lg font-bold font-manrope ${selectedCustomer.total_credit > 0 ? 'text-red-700' : 'text-slate-700'}`}>
                      ₹{(selectedCustomer.total_credit || 0).toLocaleString('en-IN')}
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* WhatsApp Summary Button - IMPROVEMENT 2 */}
              <Button 
                onClick={() => sendWhatsAppSummary(selectedCustomer)}
                className="w-full bg-green-600 hover:bg-green-700 mb-4"
                data-testid="whatsapp-summary-btn"
                disabled={!selectedCustomer.mobile_number}
              >
                <MessageCircle className="h-4 w-4 mr-2" />
                {language === 'hi' ? 'WhatsApp पर समरी भेजें' : 'Send Summary on WhatsApp'}
              </Button>

              {/* Activity Timeline - IMPROVEMENT 3 */}
              <div className="border-t pt-4">
                <h3 className="font-semibold text-slate-900 font-manrope mb-3">
                  {language === 'hi' ? 'गतिविधि टाइमलाइन' : 'Activity Timeline'}
                </h3>
                
                {/* Activity Filters */}
                <Tabs value={activityFilter} onValueChange={setActivityFilter} className="mb-4">
                  <TabsList className="grid w-full grid-cols-5 h-8">
                    <TabsTrigger value="all" className="text-xs px-1">{language === 'hi' ? 'सभी' : 'All'}</TabsTrigger>
                    <TabsTrigger value="sale" className="text-xs px-1">{language === 'hi' ? 'बिक्री' : 'Sales'}</TabsTrigger>
                    <TabsTrigger value="invoice" className="text-xs px-1">{language === 'hi' ? 'बिल' : 'Invoice'}</TabsTrigger>
                    <TabsTrigger value="credit" className="text-xs px-1">{language === 'hi' ? 'उधार' : 'Credit'}</TabsTrigger>
                    <TabsTrigger value="payment" className="text-xs px-1">{language === 'hi' ? 'भुगतान' : 'Payment'}</TabsTrigger>
                  </TabsList>
                </Tabs>

                {/* Activity List */}
                {activityLoading ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin h-6 w-6 border-2 border-[#021945] border-t-transparent rounded-full" />
                  </div>
                ) : filteredActivity.length === 0 ? (
                  <div className="text-center py-8 text-slate-500 text-sm">
                    {language === 'hi' ? 'कोई गतिविधि नहीं' : 'No activity found'}
                  </div>
                ) : (
                  <div className="space-y-3 max-h-[400px] overflow-y-auto">
                    {filteredActivity.map((item, idx) => (
                      <Card key={idx} className="hover:shadow-sm transition-shadow">
                        <CardContent className="p-3 flex items-center gap-3">
                          <div className={`p-2 rounded-full ${
                            item.type === 'sale' ? 'bg-blue-100' :
                            item.type === 'invoice' ? 'bg-purple-100' :
                            item.type === 'credit' ? 'bg-red-100' :
                            'bg-green-100'
                          }`}>
                            {item.type === 'sale' && <ShoppingCart className="h-4 w-4 text-blue-600" />}
                            {item.type === 'invoice' && <Receipt className="h-4 w-4 text-purple-600" />}
                            {item.type === 'credit' && <ArrowUpRight className="h-4 w-4 text-red-600" />}
                            {item.type === 'payment' && <ArrowDownLeft className="h-4 w-4 text-green-600" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-900 truncate">{item.description}</p>
                            <p className="text-xs text-slate-500">
                              <Calendar className="h-3 w-3 inline mr-1" />
                              {new Date(item.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                            </p>
                          </div>
                          <p className={`font-semibold font-manrope ${
                            item.type === 'payment' ? 'text-green-600' : 
                            item.type === 'credit' ? 'text-red-600' : 'text-slate-900'
                          }`}>
                            {item.type === 'payment' ? '+' : item.type === 'credit' ? '-' : ''}₹{item.amount.toLocaleString('en-IN')}
                          </p>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      <PlanExpiredModal open={showPlanExpired} onClose={() => setShowPlanExpired(false)} />
    </AetherPage>
  );
};

export default Customers;
